import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { format } from 'date-fns'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Expand } from 'lucide-react'
import { useMemo } from 'react'
import { useConversationStats } from '@/hooks/stats'
import { useDateRange } from '@/hooks/useDateRange'
import { CalendarDatePicker } from '../ui/calendar-date-picker'

const conversationTrendsConfig = {
  messages: {
    label: 'Messages',
    color: 'hsl(var(--chart-1))',
  },
  conversations: {
    label: 'Conversations',
    color: 'hsl(var(--chart-2))',
  },
} satisfies ChartConfig

const ConversationTrendsChart = () => {
  const { dateRange, setDateRange, formattedRange } = useDateRange()

  const { data: conversationStats, isLoading: isConversationStatsLoading } = useConversationStats({
    startDate: formattedRange.startDate,
    endDate: formattedRange.endDate,
    enabled: Boolean(dateRange.from && dateRange.to),
  })
  const conversationChartData = useMemo(() => {
    if (!conversationStats?.periodStats.dailyStats) return []
    return Object.entries(conversationStats.periodStats.dailyStats).map(([date, stats]) => ({
      date,
      conversations: stats.totalConversations,
      messages: stats.totalMessages,
      users: stats.uniqueUsers,
    }))
  }, [conversationStats])

  return (
    <Dialog>
      <Card className="w-full min-w-0 lg:w-auto">
        <CardHeader className="flex flex-col items-start justify-between space-y-0 pb-2">
          <div className="flex items-center justify-between w-full">
            <CardTitle>Conversation Trends</CardTitle>
            <DialogTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 data-[state=open]:bg-muted" aria-label="Open">
                <Expand className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </div>
          <div className="flex max-sm:flex-col items-center justify-between w-full">
            <CardDescription>Daily conversation and message activity</CardDescription>
            <CalendarDatePicker variant="outline" date={dateRange} onDateSelect={setDateRange} />
          </div>
        </CardHeader>
        <CardContent className="min-w-0">
          {isConversationStatsLoading ? (
            <Skeleton className="h-[18.75rem] w-full" />
          ) : (
            <ChartContainer config={conversationTrendsConfig} className="h-[18.75rem] w-full min-w-0 overflow-hidden">
              <AreaChart accessibilityLayer data={conversationChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => format(new Date(value + 'T00:00:00'), 'MMM dd')}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Area
                  type="monotone"
                  dataKey="messages"
                  stackId="1"
                  stroke="var(--color-messages)"
                  fill="var(--color-messages)"
                  fillOpacity={0.4}
                />
                <Area
                  type="monotone"
                  dataKey="conversations"
                  stackId="2"
                  stroke="var(--color-conversations)"
                  fill="var(--color-conversations)"
                  fillOpacity={0.4}
                />
              </AreaChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
      <DialogContent className="sm:max-w-[90%] max-h-[80vh] overflow-auto">
        <DialogHeader>
          <CardHeader className="p-0">
            <DialogTitle asChild>
              <CardTitle>Conversation Trends</CardTitle>
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex max-sm:flex-col justify-between">
                <CardDescription>Daily conversation and message activity</CardDescription>
                <CalendarDatePicker variant="outline" date={dateRange} onDateSelect={setDateRange} />
              </div>
            </DialogDescription>
          </CardHeader>
        </DialogHeader>
        {isConversationStatsLoading ? (
          <Skeleton className="h-[37.5rem] w-full" />
        ) : (
          <ChartContainer config={conversationTrendsConfig} className="h-[37.5rem] w-full">
            <AreaChart data={conversationChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => format(new Date(value), 'MMM dd')}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Area
                type="monotone"
                dataKey="messages"
                stackId="1"
                stroke="var(--color-messages)"
                fill="var(--color-messages)"
                fillOpacity={0.4}
              />
              <Area
                type="monotone"
                dataKey="conversations"
                stackId="2"
                stroke="var(--color-conversations)"
                fill="var(--color-conversations)"
                fillOpacity={0.4}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ConversationTrendsChart
